import { Box, buttonClasses, Link, linkClasses, styled } from '@mui/material';
import { PublicButton } from 'public/components/common/button/button';
import { config } from 'public/styles/config';
import { Trans, useTranslation } from 'react-i18next';
import { FormCertificateFilePicker } from 'public/components/certificates/forms/form-certificate-file-picker/form-certificate-file-picker';
import { FormTermsAndConditionsCheck } from 'public/components/common/forms/form-terms-and-conditions-check/form-terms-and-conditions-check';
import { useFormContext } from 'react-hook-form';
import { ACCEPTED_FILE_TYPES, CertificateUploadFormValues } from '../types';
import { FormCertificateTypeSelect } from '../../form-certificate-type-select/form-certificate-type-select';
import { useCertificateFormLogic } from '../hooks/use-certificate-form-logic';

export const CertificateFormStep = () => {
  const { t } = useTranslation('web-common');
  const {
    formState: { isValid, isSubmitting },
  } = useFormContext<CertificateUploadFormValues>();

  const { accreditationTypeOptions, certificateItems } = useCertificateFormLogic();

  return (
    <StyledContainer>
      <FormCertificateTypeSelect<CertificateUploadFormValues>
        name="accreditationType"
        items={accreditationTypeOptions}
        label={t('upload-certificate-screen.select-accreditation-type.label')}
        defaultLabel={t('upload-certificate-screen.select-accreditation-type.default-label')}
        required
      />

      <FormCertificateTypeSelect<CertificateUploadFormValues>
        name="certificate"
        items={certificateItems}
        label={t('upload-certificate-screen.select-certificate.label')}
        defaultLabel={t('upload-certificate-screen.select-certificate.default-label')}
        required
      />

      <FormCertificateFilePicker<CertificateUploadFormValues>
        name="certificateFile"
        label={t('upload-certificate-screen.upload-certificate.label')}
        title={t('upload-certificate-screen.upload-certificate.button-label')}
        fileType={ACCEPTED_FILE_TYPES}
        required
      />

      <FormTermsAndConditionsCheck name="termsAndConditionsAccepted">
        <Trans
          i18nKey="upload-certificate-screen.terms-and-conditions.agreement"
          components={[
            <StyledLink
              href={t('upload-certificate-screen.terms-and-conditions.link')}
              target="_blank"
              key="terms-and-conditions-link"
            />,
          ]}
          ns="web-common"
        ></Trans>
      </FormTermsAndConditionsCheck>

      <CenteredPublicButton type="submit" variant="contained" disabled={!isValid || isSubmitting}>
        {t('upload-certificate-screen.submit-button.label')}
      </CenteredPublicButton>
    </StyledContainer>
  );
};

const StyledContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: 560,
  backgroundColor: config.colors.background,
  borderRadius: theme.spacing(4),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(6),
  padding: theme.spacing(6, 8),
}));

const StyledLink = styled(Link)(({ theme }) => ({
  [`&.${linkClasses.root}`]: {
    color: theme.palette.lwBlack[100],
    textDecoration: 'underline',
    fontVariationSettings: '"wght" 600',
  },
}));

export const CenteredPublicButton = styled(PublicButton)({
  [`&.${buttonClasses.root}`]: {
    width: '80%',
    alignSelf: 'center',
  },
});
